/**
 * Núcleo puro de las autorizaciones de trabajo de un partner por horas
 * (change `partners-por-horas`, D5 y D7).
 *
 * Tres cosas, todas sin red ni estado:
 *
 *  1. El PRESUPUESTO: total = horas × valor de la hora × recargo, redondeado a
 *     2 decimales (mitad hacia arriba, igual que `round(numeric, 2)` de
 *     Postgres). El recargo (>= 1, 1,00 = sin recargo) multiplica la plata, no
 *     las horas: el tope de publicación sigue siendo en horas reales.
 *  2. La HUELLA del presupuesto (D5): SHA-256 de un texto canónico. La calcula
 *     este módulo al mostrar el pedido y `core.huella_autorizacion` al firmar;
 *     si no coinciden, la firma se rechaza. El vector fijo de los tests y el del
 *     test RLS 660 tienen que dar el mismo hash: si tocás el formato, cambian los
 *     dos o ninguno (versión `v1`).
 *  3. Las HORAS de una autorización con sus excedentes y la DECISIÓN de
 *     publicación de una entrada (la pantalla la reproduce para ofrecer el
 *     excedente; el que decide de verdad es el disparador del portal, D7).
 *
 * Toda la aritmética de horas y dinero se hace en centésimas enteras: nunca se
 * suman ni se comparan decimales de punto flotante.
 */

export const MONEDAS_AUTORIZACION = ['USD', 'EUR', 'ARS'] as const;
export type MonedaAutorizacion = (typeof MONEDAS_AUTORIZACION)[number];

export type EstadoAutorizacion = 'pendiente' | 'autorizada' | 'rechazada' | 'anulada';

/** Contenido que se firma: lo único que entra a la huella. */
export interface PresupuestoAutorizacion {
  titulo: string;
  alcance: string;
  horas: number;
  valorHora: number;
  /** Multiplicador del valor de la hora (>= 1). 1 = sin recargo. */
  recargo: number;
  moneda: MonedaAutorizacion;
}

// ─── Aritmética en centésimas ──────────────────────────────────────────────

/** Pasa un decimal de a lo sumo 2 cifras a centésimas enteras. */
export function aCentesimas(n: number): number {
  return Math.round(n * 100);
}

/** `n.toFixed(2)` pero desde centésimas enteras: nunca notación científica. */
function centesimasATexto(c: bigint): string {
  const negativo = c < 0n;
  const abs = negativo ? -c : c;
  const entero = abs / 100n;
  const resto = abs % 100n;
  return `${negativo ? '-' : ''}${entero.toString()}.${resto.toString().padStart(2, '0')}`;
}

/**
 * Total del presupuesto en centésimas: round(horas × valor × recargo, 2), mitad
 * hacia arriba. Con BigInt: las tres cantidades en centésimas dan millonésimas
 * exactas, y se divide por 10.000 sumando 5.000 para redondear.
 */
function totalEnCentesimas(horas: number, valorHora: number, recargo: number): bigint {
  const millonesimas =
    BigInt(aCentesimas(horas)) * BigInt(aCentesimas(valorHora)) * BigInt(aCentesimas(recargo));
  return (millonesimas + 5000n) / 10000n;
}

/** Total del presupuesto: horas × valor de la hora × recargo, 2 decimales. */
export function totalPresupuesto(horas: number, valorHora: number, recargo = 1): number {
  return Number(totalEnCentesimas(horas, valorHora, recargo)) / 100;
}

/** Número con exactamente dos decimales, punto decimal, sin separador de miles. */
export function decimal2(n: number): string {
  return centesimasATexto(BigInt(aCentesimas(n)));
}

// ─── Huella canónica (D5) ──────────────────────────────────────────────────

const codificador = new TextEncoder();

function largoUtf8(texto: string): number {
  return codificador.encode(texto).length;
}

/**
 * Texto canónico del presupuesto:
 *
 *   v1\n
 *   <bytes>:<título>\n <bytes>:<alcance>\n <bytes>:<horas>\n
 *   <bytes>:<valor de la hora>\n <bytes>:<recargo>\n <bytes>:<moneda>\n
 *   <bytes>:<total>\n
 *
 * Los textos van tal cual están guardados (sin recortar); horas, valor,
 * recargo y total con dos decimales. El prefijo de largo en bytes UTF-8 impide que un contenido
 * imite el corte entre campos.
 */
export function textoCanonico(p: PresupuestoAutorizacion): string {
  const campos = [
    p.titulo,
    p.alcance,
    decimal2(p.horas),
    decimal2(p.valorHora),
    decimal2(p.recargo),
    p.moneda,
    centesimasATexto(totalEnCentesimas(p.horas, p.valorHora, p.recargo)),
  ];
  return `v1\n${campos.map((c) => `${largoUtf8(c)}:${c}\n`).join('')}`;
}

/** SHA-256 en hexadecimal minúscula del texto canónico del presupuesto. */
export async function huellaPresupuesto(p: PresupuestoAutorizacion): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', codificador.encode(textoCanonico(p)));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── Horas disponibles de una autorización ─────────────────────────────────

/** Lo mínimo de una autorización para contar horas. */
export interface HorasDeAutorizacion {
  estado: EstadoAutorizacion;
  horas: number;
}

export interface HorasDisponibles {
  /** Horas de la autorización madre más las de sus excedentes autorizados. */
  autorizadas: number;
  /** Horas ya publicadas contra la madre. */
  publicadas: number;
  /** autorizadas − publicadas, nunca por debajo de cero. */
  disponibles: number;
}

/**
 * Horas de una autorización madre con sus excedentes. Solo cuentan las
 * autorizadas: un pedido pendiente, rechazado o anulado no habilita horas.
 * `publicadas` es la suma de horas publicadas imputadas a la madre (D7).
 */
export function horasDisponibles(
  madre: HorasDeAutorizacion,
  excedentes: readonly HorasDeAutorizacion[],
  publicadas: number,
): HorasDisponibles {
  const autorizadasC =
    (madre.estado === 'autorizada' ? aCentesimas(madre.horas) : 0) +
    excedentes.reduce((s, e) => s + (e.estado === 'autorizada' ? aCentesimas(e.horas) : 0), 0);
  const publicadasC = aCentesimas(publicadas);
  return {
    autorizadas: autorizadasC / 100,
    publicadas: publicadasC / 100,
    disponibles: Math.max(0, autorizadasC - publicadasC) / 100,
  };
}

// ─── Decisión de publicación ───────────────────────────────────────────────

export type DecisionPublicacion =
  /** Se puede publicar; quedan `restantes` horas disponibles después. */
  | { tipo: 'publica'; restantes: number }
  /** La entrada no tiene autorización vigente: ofrecer pedirla. */
  | { tipo: 'sin_autorizacion' }
  /** Supera lo autorizado por `horasFaltantes`: ofrecer pedir el excedente. */
  | { tipo: 'excede'; horasFaltantes: number };

/**
 * ¿Se puede publicar una entrada de `horasEntrada` contra `madre`?
 *
 * - Sin autorización, o con una que no está `autorizada`: `sin_autorizacion`.
 * - Si las horas de la entrada superan las disponibles: `excede`, con las horas
 *   que faltan autorizar (el excedente se pide aparte y hasta que se autorice
 *   no se publica ni se cobra).
 * - Si no: `publica`.
 */
export function decidirPublicacion(
  madre: HorasDeAutorizacion | null | undefined,
  excedentes: readonly HorasDeAutorizacion[],
  publicadas: number,
  horasEntrada: number,
): DecisionPublicacion {
  if (!madre || madre.estado !== 'autorizada') return { tipo: 'sin_autorizacion' };
  const { disponibles } = horasDisponibles(madre, excedentes, publicadas);
  const dispC = aCentesimas(disponibles);
  const entradaC = aCentesimas(horasEntrada);
  if (entradaC > dispC) return { tipo: 'excede', horasFaltantes: (entradaC - dispC) / 100 };
  return { tipo: 'publica', restantes: (dispC - entradaC) / 100 };
}

// ── Decisión en minutos (la cuenta del disparador del portal) ──────────────

export type DecisionEnMinutos =
  | { tipo: 'publica'; restantesMin: number }
  | { tipo: 'sin_autorizacion' }
  /** Supera lo autorizado por `faltanMin` minutos enteros (igual que `faltan_minutos` del portal). */
  | { tipo: 'excede'; faltanMin: number };

/**
 * Igual que `decidirPublicacion`, pero compara en MINUTOS como el disparador del
 * portal (50 min no son 0,83 h): pozo = Σ horas autorizadas × 60 (madre y
 * excedentes autorizados), usado = minutos ya publicados contra el conjunto.
 * Las horas llegan con dos decimales, así que el pozo se calcula en centésimas
 * de hora × 0,6 minutos, sin pasar por punto flotante hasta el final.
 */
export function decidirPublicacionEnMinutos(
  madre: HorasDeAutorizacion | null | undefined,
  excedentes: readonly HorasDeAutorizacion[],
  minutosPublicados: number,
  minutosEntrada: number,
): DecisionEnMinutos {
  if (!madre || madre.estado !== 'autorizada') return { tipo: 'sin_autorizacion' };
  const centesimas =
    aCentesimas(madre.horas) +
    excedentes.reduce((s, e) => s + (e.estado === 'autorizada' ? aCentesimas(e.horas) : 0), 0);
  // pozo en minutos = centésimas × 60 / 100; se compara multiplicado por 100.
  const falta100 = (minutosPublicados + minutosEntrada) * 100 - centesimas * 60;
  if (falta100 > 0) return { tipo: 'excede', faltanMin: Math.round(falta100 / 100) || 1 };
  return { tipo: 'publica', restantesMin: Math.max(0, Math.floor(-falta100 / 100)) };
}
